import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import SystemLLMPromptManagement from '../../components/admin/SystemLLMPromptManagement';
import NoteTemplateManagementPage from './NoteTemplateManagementPage';
import NoteTemplateAssociationsPage from './NoteTemplateAssociationsPage';
import SidebarPanel from '../../components/admin/SidebarPanel';
import AgGridTable from '../../components/AgGridTable';

const NoteTemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sidePanel, setSidePanel] = useState({
    isOpen: false,
    type: null
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedAssociationTemplateId, setSelectedAssociationTemplateId] = useState(null);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await adminApi.fetchAdminNoteTemplates();
        
        // Based on the backend code, the response.data is directly the array of templates
        if (Array.isArray(response.data)) {
          // Sort templates by creation date, newest first
          const sortedTemplates = response.data.sort((a, b) =>
            new Date(b.created_at) - new Date(a.created_at)
          );
          setTemplates(sortedTemplates);
        } else {
          setError('Invalid response format from server');
        }
      } catch (err) {
        console.error('Error fetching templates:', err);
        if (err.response && err.response.status === 403) {
          setError('Permission denied. This feature requires proper authentication and authorization.');
        } else {
          setError(`Error: ${err.message}`);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  // Handle side panel animation
  useEffect(() => {
    const sidebar = document.querySelector('.sidebar-panel');
    if (sidebar) {
      if (sidePanel.isOpen) {
        sidebar.classList.add('open');
      } else {
        sidebar.classList.remove('open');
      }
    }
  }, [sidePanel.isOpen]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Get template type badge class
  const getTemplateTypeBadgeClass = (templateType) => {
    switch (templateType) {
      case 'SOAP':
        return 'badge bg-primary';
      case 'H&P':
        return 'badge bg-success';
      case 'Progress':
        return 'badge bg-info';
      case 'Discharge':
        return 'badge bg-warning';
      case 'Consultation':
        return 'badge bg-secondary';
      case 'Nurse':
        return 'badge bg-info';
      case 'Social Worker':
        return 'badge bg-secondary';
      default:
        return 'badge bg-light text-dark';
    }
  };

  // Handle new template creation
  const handleNewTemplate = () => {
    setSidePanel({ isOpen: true, type: 'createTemplate' });
  };

  // Handle template editing
  const handleEditTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    setSidePanel({ isOpen: true, type: 'editTemplate' });
  };

  // Handle template save
  const handleTemplateSave = async () => {
    // Refresh templates list
    try {
      const response = await adminApi.fetchAdminNoteTemplates();
      if (Array.isArray(response.data)) {
        const sortedTemplates = response.data.sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        );
        setTemplates(sortedTemplates);
      }
    } catch (err) {
      console.error('Error refreshing templates:', err);
    }
    // Close sidebar
    setSidePanel({ isOpen: false, type: null });
    setSelectedTemplateId(null);
  };

  // Handle template deletion
  const handleDeleteTemplate = async (templateId, templateName) => {
    if (window.confirm(`Are you sure you want to delete the template "${templateName}"? This action cannot be undone.`)) {
      try {
        await adminApi.delete(`/admin/note/templates/${templateId}/`);
        // Refresh the templates list
        setTemplates(templates.filter(t => t.id !== templateId));
      } catch (err) {
        console.error('Error deleting template:', err);
        setError(`Error deleting template: ${err.message}`);
      }
    }
  };

  // Handle template associations
  const handleTemplateAssociations = (templateId) => {
    setSelectedAssociationTemplateId(templateId);
    setSidePanel({ isOpen: true, type: 'associations' });
  };

  // Handle associations save
  const handleAssociationsSave = async () => {
    // Refresh templates list
    try {
      const response = await adminApi.fetchAdminNoteTemplates();
      if (Array.isArray(response.data)) {
        const sortedTemplates = response.data.sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        );
        setTemplates(sortedTemplates);
      }
    } catch (err) {
      console.error('Error refreshing templates:', err);
    }
    // Close sidebar
    setSidePanel({ isOpen: false, type: null });
    setSelectedAssociationTemplateId(null);
  };

  return (
    <>
      {/* Page Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">Note Templates Management</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><span>Admin</span></li>
                <li className="breadcrumb-item active">Note Templates</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      
      <div className="d-flex justify-content-end align-items-center mb-4">
        <div>
          <button className="btn btn-primary me-2" onClick={handleNewTemplate}>
            <i className="fas fa-plus-circle me-1"></i> New Template
          </button>
          <button 
            className="btn btn-outline-warning" 
            onClick={() => setSidePanel({ isOpen: true, type: 'llmPrompts' })}
          >
            <i className="fas fa-robot me-1"></i> Master LLM Prompts
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* Templates Table */}
      <div className="card">
          {loading ? (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2">Loading note templates...</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="alert text-secondary text-center">
              No note templates found. Create your first template to get started.
            </div>
          ) : (
            <AgGridTable
              rowData={templates}
              columnDefs={[
                {
                  headerName: 'Template Name',
                  field: 'name',
                  width: 200,
                  cellRenderer: (params) => {
                    if (!params.data) return null;
                    return (
                      <div className="d-flex align-items-center h-100">
                          <h6 className="mb-0">{params.data.name || 'Untitled Template'}</h6>
                          {params.data.description && (
                            <p className="text-muted mb-0 small">{params.data.description}</p>
                          )}
                      </div>
                    );
                  }
                },
                {
                  headerName: 'Type',
                  field: 'template_type',
                  width: 120,
                  cellRenderer: (params) => {
                    if (!params.data) return null;
                    return (
                      <span className={getTemplateTypeBadgeClass(params.data.template_type)}>
                        {params.data.template_type || 'Unknown'}
                      </span>
                    );
                  }
                },
                {
                  headerName: 'Provider',
                  field: 'provider',
                  width: 150,
                  valueGetter: (p) => p.data?.provider 
                    ? `${p.data.provider.first_name} ${p.data.provider.last_name}` 
                    : 'Global Template',
                  cellRenderer: (params) => {
                    if (!params.data) return null;
                    if (params.data.provider) {
                      return (
                        <Link to={`/admin/providers/${params.data.provider.provider_id}`} className="text-body fw-bold">
                          {`${params.data.provider.first_name} ${params.data.provider.last_name}`}
                        </Link>
                      );
                    }
                    return <span className="text-muted">Global Template</span>;
                  }
                },
                {
                  headerName: 'Health Systems',
                  field: 'health_systems',
                  width: 150,
                  valueGetter: (p) => p.data?.health_systems?.map(hs => hs.name || hs).join(', ') || 'None',
                  cellRenderer: (params) => {
                    if (!params.data) return null;
                    const hs = params.data.health_systems;
                    if (hs && hs.length > 0) {
                      return (
                        <div className="d-flex flex-wrap align-items-center gap-1 h-100">
                          {hs.slice(0, 2).map((h, i) => (
                            <span key={i} className="badge bg-info small">{h.name || h}</span>
                          ))}
                          {hs.length > 2 && <span className="text-muted small">+{hs.length - 2} more</span>}
                        </div>
                      );
                    }
                    return (
                      <div className="d-flex align-items-center h-100">
                        <span className="text-muted small">None</span>
                      </div>
                    );
                  }
                },
                {
                  headerName: 'Specialties',
                  field: 'specialties',
                  width: 150,
                  valueGetter: (p) => p.data?.specialties?.map(s => s.name || s).join(', ') || 'None',
                  cellRenderer: (params) => {
                    if (!params.data) return null;
                    const specs = params.data.specialties;
                    if (specs && specs.length > 0) {
                      return (
                        <div className="d-flex flex-wrap gap-1">
                          {specs.slice(0, 2).map((s, i) => (
                            <span key={i} className="badge bg-secondary small">{s.name || s}</span>
                          ))}
                          {specs.length > 2 && <span className="text-muted small">+{specs.length - 2} more</span>}
                        </div>
                      );
                    }
                    return <span className="text-muted small">None</span>;
                  }
                },
                {
                  headerName: 'Status',
                  field: 'is_active',
                  width: 100,
                  valueGetter: (p) => p.data?.is_active ? 'Active' : 'Inactive',
                  cellRenderer: (params) => {
                    if (!params.data) return null;
                    return (
                      <span className={`badge ${params.data.is_active ? 'bg-success' : 'bg-danger'}`}>
                        {params.data.is_active ? 'Active' : 'Inactive'}
                      </span>
                    );
                  }
                },
                {
                  headerName: 'Updated',
                  field: 'updated_at',
                  width: 150,
                  valueGetter: (p) => formatDate(p.data?.updated_at)
                },
                {
                  headerName: 'Actions',
                  field: 'actions',
                  width: 180,
                  sortable: false,
                  filter: false,
                  cellRenderer: (params) => {
                    if (!params.data) return null;
                    return (
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          className="btn btn-outline-primary"
                          title="View Template"
                          onClick={() => handleEditTemplate(params.data.id)}
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          title="Edit Template"
                          onClick={() => handleEditTemplate(params.data.id)}
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          className="btn btn-outline-info"
                          title="Manage Associations"
                          onClick={() => handleTemplateAssociations(params.data.id)}
                        >
                          <i className="fas fa-link"></i>
                        </button>
                        <button
                          className="btn btn-outline-danger"
                          title="Delete Template"
                          onClick={() => handleDeleteTemplate(params.data.id, params.data.name)}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    );
                  }
                }
              ]}
              defaultColDef={{
                flex: 1,
                minWidth: 100,
                resizable: true,
                sortable: true,
                filter: true,
              }}
              gridOptions={{
                autoSizeStrategy: { type: 'fitGridWidth' }
              }}
              getRowId={(params) => `${params.data.id}`}
            />
          )}
      </div>

      {/* SystemLLMPromptManagement Side Panel */}
      <SidebarPanel
        isOpen={sidePanel.isOpen && sidePanel.type === 'llmPrompts'}
        onClose={() => setSidePanel({ isOpen: false, type: null })}
        title={<><i className="fas fa-robot me-2 text-warning"></i>Master LLM Prompts</>}
      >
        <SystemLLMPromptManagement
          isOpen={true}
          onClose={() => setSidePanel({ isOpen: false, type: null })}
        />
      </SidebarPanel>

      {/* Edit Template Side Panel */}
      <SidebarPanel
        isOpen={sidePanel.isOpen && sidePanel.type === 'editTemplate'}
        onClose={() => {
          setSidePanel({ isOpen: false, type: null });
          setSelectedTemplateId(null);
        }}
        title={<><i className="fas fa-edit me-2 text-primary"></i>Edit Note Template</>}
      >
        {selectedTemplateId && (
          <NoteTemplateManagementPage
            templateId={selectedTemplateId}
            isEmbedded={true}
            onClose={() => {
              setSidePanel({ isOpen: false, type: null });
              setSelectedTemplateId(null);
            }}
            onSave={handleTemplateSave}
          />
        )}
      </SidebarPanel>

      {/* Create Template Side Panel */}
      <SidebarPanel
        isOpen={sidePanel.isOpen && sidePanel.type === 'createTemplate'}
        onClose={() => setSidePanel({ isOpen: false, type: null })}
        title={<><i className="fas fa-plus-circle me-2 text-success"></i>Create New Note Template</>}
      >
        <NoteTemplateManagementPage
          isEmbedded={true}
          onClose={() => setSidePanel({ isOpen: false, type: null })}
          onSave={handleTemplateSave}
        />
      </SidebarPanel>

      {/* Template Associations Side Panel */}
      <SidebarPanel
        isOpen={sidePanel.isOpen && sidePanel.type === 'associations'}
        onClose={() => {
          setSidePanel({ isOpen: false, type: null });
          setSelectedAssociationTemplateId(null);
        }}
        title={<><i className="fas fa-link me-2 text-info"></i>Manage Template Associations</>}
      >
        {selectedAssociationTemplateId && (
          <NoteTemplateAssociationsPage
            templateId={selectedAssociationTemplateId}
            isEmbedded={true}
            onClose={() => {
              setSidePanel({ isOpen: false, type: null });
              setSelectedAssociationTemplateId(null);
            }}
            onSave={handleAssociationsSave}
          />
        )}
      </SidebarPanel>
    </>
  );
};

export default NoteTemplatesPage;